import { useState, useEffect, useCallback } from 'react';
import { hotelApi } from '../../hotelApi';

const ROOM_TYPES = ['Standard', 'Deluxe', 'Suite', 'AC', 'Non-AC', 'Family', 'Other'];
const EMPTY_ROOM = { roomNo: '', roomType: 'Standard', capacity: 2, ratePerNight: '', amenities: '', status: 'available' };
const WEEK_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTH_NAMES = ['January','February','March','April','May','June',
                     'July','August','September','October','November','December'];

function toISO(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

function buildCalendar(year, month) {
  const firstDay = new Date(year, month, 1);
  const lastDate = new Date(year, month + 1, 0).getDate();
  // Monday-start: 0=Mon … 6=Sun
  const startOffset = (firstDay.getDay() + 6) % 7;
  const weeks = [];
  let week = Array(startOffset).fill(null);
  for (let d = 1; d <= lastDate; d++) {
    week.push(new Date(year, month, d));
    if (week.length === 7) { weeks.push(week); week = []; }
  }
  if (week.length > 0) {
    while (week.length < 7) week.push(null);
    weeks.push(week);
  }
  return weeks;
}

function RoomCalendar({ rooms, bookings }) {
  const today = new Date();
  const [year, setYear]   = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [tooltip, setTooltip] = useState(null); // { x, y, content }

  const prevMonth = () => {
    if (month === 0) { setYear(y => y - 1); setMonth(11); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 11) { setYear(y => y + 1); setMonth(0); }
    else setMonth(m => m + 1);
  };
  const goToday = () => { setYear(today.getFullYear()); setMonth(today.getMonth()); };

  const getRoomDayStatus = (roomId, dateStr) => {
    for (const b of bookings) {
      if (String(b.room_id) !== String(roomId)) continue;
      if (b.status === 'cancelled') continue;
      if (dateStr === b.check_in)  return 'checkin';
      if (dateStr === b.check_out) return 'checkout';
      if (dateStr > b.check_in && dateStr < b.check_out) return 'booked';
    }
    return 'free';
  };

  const getBookingForRoom = (roomId, dateStr) => {
    for (const b of bookings) {
      if (String(b.room_id) !== String(roomId)) continue;
      if (b.status === 'cancelled') continue;
      if (dateStr >= b.check_in && dateStr <= b.check_out) return b;
    }
    return null;
  };

  const STATUS_STYLE = {
    free:     { bg: '#e6f4ea', color: '#137333', border: '#b7dfbe' },
    booked:   { bg: '#fce8e6', color: '#c5221f', border: '#f5c6c6' },
    checkin:  { bg: '#e8f0fe', color: '#1a73e8', border: '#c5d8fd' },
    checkout: { bg: '#fef3c7', color: '#b45309', border: '#fde68a' },
  };

  const todayStr = toISO(today);
  const weeks = buildCalendar(year, month);

  return (
    <div style={{ fontFamily: 'Inter,system-ui,sans-serif' }}>
      {/* Month navigation */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <button onClick={prevMonth}
          style={{ background: '#f1f3f4', border: '1px solid #e8eaed', borderRadius: 8,
            padding: '6px 14px', cursor: 'pointer', fontWeight: 700, fontSize: 16 }}>‹</button>
        <div style={{ fontWeight: 700, fontSize: 18, color: '#0d3321', minWidth: 160, textAlign: 'center' }}>
          {MONTH_NAMES[month]} {year}
        </div>
        <button onClick={nextMonth}
          style={{ background: '#f1f3f4', border: '1px solid #e8eaed', borderRadius: 8,
            padding: '6px 14px', cursor: 'pointer', fontWeight: 700, fontSize: 16 }}>›</button>
        <button onClick={goToday}
          style={{ background: '#137333', color: '#fff', border: 'none', borderRadius: 8,
            padding: '6px 14px', cursor: 'pointer', fontWeight: 600, fontSize: 13, marginLeft: 4 }}>
          Today
        </button>
        {/* Legend */}
        <div style={{ display: 'flex', gap: 10, marginLeft: 'auto', flexWrap: 'wrap', fontSize: 12 }}>
          {[['checkin','#1a73e8','Check-in'],['checkout','#b45309','Check-out'],
            ['booked','#c5221f','Booked'],['free','#137333','Free']].map(([k, c, lbl]) => (
            <span key={k} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 12, height: 12, borderRadius: 3,
                background: STATUS_STYLE[k].bg, border: `1px solid ${STATUS_STYLE[k].border}`, display: 'inline-block' }}/>
              <span style={{ color: c, fontWeight: 600 }}>{lbl}</span>
            </span>
          ))}
        </div>
      </div>

      {rooms.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 20px', color: '#888' }}>No rooms configured.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 700 }}>
            <thead>
              <tr>
                {WEEK_DAYS.map(d => (
                  <th key={d} style={{
                    padding: '8px 4px', textAlign: 'center', fontSize: 12, fontWeight: 700,
                    color: (d === 'Sat' || d === 'Sun') ? '#c5221f' : '#5f6368',
                    background: '#f8faf8', borderBottom: '2px solid #e8eaed',
                    width: `${100/7}%`,
                  }}>{d}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {weeks.map((week, wi) => (
                <tr key={wi}>
                  {week.map((day, di) => {
                    if (!day) return (
                      <td key={di} style={{ background: '#fafafa', border: '1px solid #f1f3f4',
                        verticalAlign: 'top', minHeight: 80, padding: 4 }} />
                    );
                    const dateStr = toISO(day);
                    const isToday = dateStr === todayStr;
                    const isPast  = dateStr < todayStr;
                    const isWeekend = di >= 5;

                    return (
                      <td key={di} style={{
                        border: '1px solid #e8eaed',
                        verticalAlign: 'top', padding: '6px 4px',
                        background: isToday ? '#e8f5e9' : isPast ? '#fafafa' : '#fff',
                        minHeight: 90,
                      }}>
                        {/* Day number */}
                        <div style={{
                          fontSize: 13, fontWeight: isToday ? 800 : 600,
                          color: isToday ? '#137333' : isPast ? '#bbb' : isWeekend ? '#c5221f' : '#202124',
                          marginBottom: 4, textAlign: 'right', paddingRight: 2,
                        }}>
                          {isToday ? (
                            <span style={{ background: '#137333', color: '#fff',
                              borderRadius: '50%', width: 22, height: 22,
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                              {day.getDate()}
                            </span>
                          ) : day.getDate()}
                        </div>

                        {/* Room chips */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {rooms.map(r => {
                            const st = getRoomDayStatus(r.id, dateStr);
                            if (st === 'free' && isPast) return null;
                            const ss = STATUS_STYLE[st];
                            const bk = st !== 'free' ? getBookingForRoom(r.id, dateStr) : null;
                            const label = st === 'checkin' ? '▶' : st === 'checkout' ? '◀' : st === 'booked' ? '■' : '○';
                            return (
                              <div key={r.id}
                                onMouseEnter={e => {
                                  if (!bk) return;
                                  setTooltip({ x: e.clientX, y: e.clientY,
                                    content: `${r.room_no} · ${bk.guest_name || 'Guest'}\n${bk.check_in} → ${bk.check_out}` });
                                }}
                                onMouseLeave={() => setTooltip(null)}
                                style={{
                                  background: ss.bg, color: ss.color, border: `1px solid ${ss.border}`,
                                  borderRadius: 4, padding: '1px 5px', fontSize: 11, fontWeight: 700,
                                  display: 'flex', alignItems: 'center', gap: 3, cursor: bk ? 'help' : 'default',
                                  whiteSpace: 'nowrap',
                                }}>
                                <span style={{ fontSize: 9 }}>{label}</span>
                                <span>{r.room_no}</span>
                              </div>
                            );
                          })}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Hover tooltip */}
      {tooltip && (
        <div style={{
          position: 'fixed', left: tooltip.x + 12, top: tooltip.y - 8, zIndex: 9999,
          background: '#202124', color: '#fff', borderRadius: 8, padding: '8px 12px',
          fontSize: 12, lineHeight: 1.6, whiteSpace: 'pre', boxShadow: '0 4px 16px rgba(0,0,0,.35)',
          pointerEvents: 'none',
        }}>
          {tooltip.content}
        </div>
      )}
    </div>
  );
}

export default function HotelRooms({ role }) {
  const [tab, setTab]         = useState('rooms');
  const [rooms, setRooms]     = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm]       = useState(EMPTY_ROOM);
  const [editId, setEditId]   = useState(null);
  const [saving, setSaving]   = useState(false);
  const [err, setErr]         = useState('');

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([hotelApi.getRooms(), hotelApi.getRoomBookings()])
      .then(([r, b]) => { setRooms(r || []); setBookings(b || []); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const fmtRs = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });

  const saveRoom = async () => {
    if (!form.roomNo.trim()) { setErr('Room No is required'); return; }
    setSaving(true); setErr('');
    try {
      if (editId) await hotelApi.updateRoom(editId, form);
      else await hotelApi.createRoom(form);
      setForm(EMPTY_ROOM); setEditId(null); load();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const deleteRoom = async (id, no) => {
    if (!window.confirm(`Delete room "${no}"?`)) return;
    await hotelApi.deleteRoom(id); load();
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading rooms…</div>;

  return (
    <div>
      <div className="tabs" style={{ marginBottom: 16 }}>
        <button className={`tab${tab === 'rooms' ? ' active' : ''}`} onClick={() => setTab('rooms')}>
          🛏️ Rooms ({rooms.length})
        </button>
        <button className={`tab${tab === 'availability' ? ' active' : ''}`} onClick={() => setTab('availability')}>
          📅 Availability Calendar
        </button>
        {(role === 'admin' || role === 'manager') && (
          <button className={`tab${tab === 'manage' ? ' active' : ''}`} onClick={() => setTab('manage')}>
            ⚙️ Manage Rooms
          </button>
        )}
      </div>

      {tab === 'rooms' && (
        <div className="card">
          <div className="card-header">
            <div className="card-title">Rooms</div>
            {(role === 'admin' || role === 'manager') && (
              <button className="btn btn-primary" style={{ fontSize: 13, padding: '5px 14px' }}
                onClick={() => setTab('manage')}>
                ➕ Add / Manage Rooms
              </button>
            )}
          </div>
          <div className="card-body" style={{ padding: 0, overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Room No</th><th>Type</th><th>Capacity</th>
                  <th>Rate/Night</th><th>Amenities</th><th>Status</th>
                </tr>
              </thead>
              <tbody>
                {rooms.length === 0 && (
                  <tr><td colSpan={6} style={{ textAlign: 'center', color: '#888', padding: 24 }}>
                    No rooms yet.{' '}
                    {(role === 'admin' || role === 'manager')
                      ? <button style={{ background: 'none', border: 'none', color: '#137333', cursor: 'pointer', fontWeight: 700, padding: 0, fontSize: 14 }} onClick={() => setTab('manage')}>Go to ⚙️ Manage Rooms to add.</button>
                      : 'Ask your admin to add rooms.'}
                  </td></tr>
                )}
                {rooms.map(r => (
                  <tr key={r.id}>
                    <td><strong>{r.room_no}</strong></td>
                    <td style={{ fontSize: 13 }}>{r.room_type}</td>
                    <td>{r.capacity}</td>
                    <td style={{ fontWeight: 600, color: '#137333' }}>{fmtRs(r.rate_per_night)}</td>
                    <td style={{ fontSize: 12, color: '#888' }}>{r.amenities || '—'}</td>
                    <td>
                      <span style={{
                        background: r.status === 'available' ? '#e6f4ea' : r.status === 'occupied' ? '#fce8e6' : '#f1f3f4',
                        color: r.status === 'available' ? '#137333' : r.status === 'occupied' ? '#c5221f' : '#888',
                        borderRadius: 10, padding: '2px 8px', fontSize: 12, fontWeight: 600
                      }}>{r.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'availability' && (
        <div className="card">
          <div className="card-header">
            <div className="card-title">Room Availability Calendar</div>
            <button className="btn btn-secondary" style={{ fontSize: 13, padding: '5px 14px' }}
              onClick={load}>↻ Refresh</button>
          </div>
          <div className="card-body">
            <RoomCalendar rooms={rooms} bookings={bookings} />
          </div>
        </div>
      )}

      {tab === 'manage' && (
        <div>
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header">
              <div className="card-title">{editId ? 'Edit Room' : 'Add New Room'}</div>
            </div>
            <div className="card-body">
              {err && <div style={{ color: '#c5221f', marginBottom: 10, fontSize: 13 }}>⚠️ {err}</div>}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">Room No *</label>
                  <input className="form-control" value={form.roomNo}
                    onChange={e => setForm(f => ({ ...f, roomNo: e.target.value }))} placeholder="e.g. 101" />
                </div>
                <div className="form-group">
                  <label className="form-label">Type</label>
                  <select className="form-control" value={form.roomType}
                    onChange={e => setForm(f => ({ ...f, roomType: e.target.value }))}>
                    {ROOM_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Capacity</label>
                  <input className="form-control" type="number" value={form.capacity}
                    onChange={e => setForm(f => ({ ...f, capacity: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Rate/Night (₹)</label>
                  <input className="form-control" type="number" value={form.ratePerNight}
                    onChange={e => setForm(f => ({ ...f, ratePerNight: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select className="form-control" value={form.status}
                    onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                    {['available','occupied','maintenance'].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label className="form-label">Amenities</label>
                  <input className="form-control" value={form.amenities}
                    onChange={e => setForm(f => ({ ...f, amenities: e.target.value }))}
                    placeholder="WiFi, AC, TV, Mini Bar…" />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button className="btn btn-primary" onClick={saveRoom} disabled={saving}>
                  {saving ? 'Saving…' : editId ? '💾 Update' : '➕ Add Room'}
                </button>
                {editId && (
                  <button className="btn btn-secondary"
                    onClick={() => { setEditId(null); setForm(EMPTY_ROOM); setErr(''); }}>
                    Cancel
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <div className="card-title">All Rooms ({rooms.length})</div>
            </div>
            <div className="card-body" style={{ padding: 0, overflowX: 'auto' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Room No</th><th>Type</th><th>Capacity</th>
                    <th>Rate/Night</th><th>Amenities</th><th>Status</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rooms.length === 0 && (
                    <tr><td colSpan={7} style={{ textAlign: 'center', color: '#888', padding: 24 }}>No rooms yet. Add your first room above.</td></tr>
                  )}
                  {rooms.map(r => (
                    <tr key={r.id}>
                      <td><strong>{r.room_no}</strong></td>
                      <td style={{ fontSize: 13 }}>{r.room_type}</td>
                      <td>{r.capacity}</td>
                      <td style={{ fontWeight: 600, color: '#137333' }}>{fmtRs(r.rate_per_night)}</td>
                      <td style={{ fontSize: 12, color: '#888' }}>{r.amenities || '—'}</td>
                      <td>
                        <span style={{
                          background: r.status === 'available' ? '#e6f4ea' : r.status === 'occupied' ? '#fce8e6' : '#f1f3f4',
                          color: r.status === 'available' ? '#137333' : r.status === 'occupied' ? '#c5221f' : '#888',
                          borderRadius: 10, padding: '2px 8px', fontSize: 12, fontWeight: 600
                        }}>{r.status}</span>
                      </td>
                      <td>
                        <button className="btn-sm btn-secondary" style={{ marginRight: 4 }}
                          onClick={() => {
                            setForm({ roomNo: r.room_no, roomType: r.room_type, capacity: r.capacity,
                              ratePerNight: r.rate_per_night, amenities: r.amenities || '', status: r.status });
                            setEditId(r.id); setErr('');
                          }}>Edit</button>
                        <button className="btn-sm" style={{ background: '#fce8e6', color: '#c5221f', border: '1px solid #f5c6c6' }}
                          onClick={() => deleteRoom(r.id, r.room_no)}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
